import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { companyYearVars } from '../utils/copyright';
import { addFormatters } from './formatters';
import { resolveSiteLanguage } from '../utils/routing';
import {
  loadLocaleNamespace,
  loadLocaleNamespaces,
  localeNamespaces,
  type LocaleCode,
} from './loadLocale';

export { videoBaseTexts } from './loadLocale';
export { namespacesForPath } from './namespaces';

export const defaultNS = 'common';
export const defaultLanguage = 'sk';
export const supportedLanguages = ['sk', 'cs', 'da', 'en', 'de'] as const;

const isServer = typeof window === 'undefined';
const companyYears = companyYearVars();

const createLocaleBackend = () => ({
  type: 'backend' as const,
  init() {},
  read(language: string, namespace: string, callback: (error: Error | null, data: unknown) => void) {
    loadLocaleNamespace(language, namespace)
      .then((data) => callback(null, data))
      .catch((error: Error) => callback(error, false));
  },
});

// German copy is translated from English, so a key missing in de shows the
// English text rather than the Slovak source.
const fallbackLanguages: Record<string, LocaleCode[]> = {
  de: ['en'],
};

export const fallbackChain = (language: string): LocaleCode[] =>
  [...(fallbackLanguages[language] ?? [defaultLanguage])];

const sharedInit = {
  fallbackLng: { ...fallbackLanguages, default: [defaultLanguage] },
  defaultNS,
  supportedLngs: [...supportedLanguages],
  interpolation: {
    escapeValue: false,
    defaultVariables: companyYears,
  },
  react: {
    useSuspense: false,
  },
  initImmediate: false as const,
  partialBundledLanguages: true,
};

export async function ensureNamespaces(
  instance: I18nInstance,
  language: string,
  namespaces: readonly string[],
): Promise<void> {
  // A language with its own fallback chain (de → en) needs those bundles too,
  // or a missing key would render as the raw key on the client.
  await Promise.all(
    (fallbackLanguages[language] ?? []).map((code) => ensureBundles(instance, code, namespaces)),
  );
  await ensureBundles(instance, language, namespaces);
}

/** True when `ensureNamespaces` has nothing left to load for this language. */
export function hasNamespaces(
  instance: I18nInstance,
  language: string,
  namespaces: readonly string[],
): boolean {
  return [...(fallbackLanguages[language] ?? []), language].every((code) =>
    namespaces.every((namespace) => instance.hasResourceBundle(code, namespace)),
  );
}

async function ensureBundles(
  instance: I18nInstance,
  language: string,
  namespaces: readonly string[],
): Promise<void> {
  const missing = namespaces.filter((namespace) => !instance.hasResourceBundle(language, namespace));
  if (missing.length === 0) return;

  const loaded = await loadLocaleNamespaces(language, missing);
  for (const namespace of missing) {
    instance.addResourceBundle(language, namespace, loaded[namespace], true, true);
  }
}

export const initI18n = async (language?: string, namespaces: readonly string[] = ['common']) => {
  const instance = i18next.createInstance();
  const lng = language || defaultLanguage;
  const ns = Array.from(new Set(['common', ...namespaces]));

  if (!language && !isServer) {
    instance.use(LanguageDetector);
  }

  instance.use(createLocaleBackend() as never);
  instance.use(initReactI18next);

  await instance.init({
    ...sharedInit,
    lng,
    ns,
    detection: !language && !isServer ? {
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      caches: ['localStorage', 'cookie'],
      lookupQuerystring: 'lang',
      lookupCookie: 'i18next',
      lookupLocalStorage: 'i18nextLng',
      convertDetectedLanguage: (lng: string) => resolveSiteLanguage(lng) ?? lng,
    } : undefined,
  });
  addFormatters(instance);

  await ensureNamespaces(instance, lng, ns);

  if (import.meta.env.DEV) {
    await Promise.all(
      supportedLanguages.map((code) => ensureNamespaces(instance, code, ['common'])),
    );
  }

  return instance;
};

export const restoreFullResources = async (instance: I18nInstance) => {
  await Promise.all(
    supportedLanguages.flatMap((lng) =>
      localeNamespaces.map(async (namespace) => {
        const data = await loadLocaleNamespace(lng, namespace);
        instance.addResourceBundle(lng, namespace, data, true, true);
      }),
    ),
  );
};

export const createServerI18n = async (
  language: string,
  namespaces: readonly string[] = ['common'],
) => {
  const instance = i18next.createInstance();
  const lng = resolveSiteLanguage(language) ?? defaultLanguage;
  const ns = Array.from(new Set(['common', ...namespaces]));

  await instance
    .use(initReactI18next)
    .init({
      ...sharedInit,
      lng,
      ns,
    });
  addFormatters(instance);

  const languages = new Set<LocaleCode>([lng as LocaleCode, ...fallbackChain(lng)]);
  await Promise.all(
    [...languages].flatMap((code) =>
      ns.map(async (namespace) => {
        const data = await loadLocaleNamespace(code, namespace);
        instance.addResourceBundle(code, namespace, data, true, true);
      }),
    ),
  );

  await instance.changeLanguage(lng);
  return instance;
};
